import {
  FieldPath,
  FieldType,
  FormRow,
  OneOfBranchInfo,
  OptionalChildInfo,
  pathToId,
  prettify,
} from "./types";
import { enumOptionsFromSchema, getEnumFromSchema } from "./enumResolver";
import { isSecretField } from "./secrets";

export interface BuildRowsOpts {
  schema: any;
  data: any;
  dirtyIds: Set<string>;
  errorsByRowId: Map<string, { message: string; severity: "error" | "warning" }>;
  pendingDiffs?: Map<string, import("./types").BranchDiff>;
}

function primaryScalarType(schema: any): string | undefined {
  const t = schema?.type;
  if (typeof t === "string") return t;
  if (Array.isArray(t)) return t.find((x: string) => x !== "null");
  return undefined;
}

function isObjectSchema(schema: any): boolean {
  if (!schema || typeof schema !== "object") return false;
  if (Array.isArray(schema.oneOf) && schema.oneOf.length > 0) return false;
  if (primaryScalarType(schema) === "object") return true;
  if (schema.properties && typeof schema.properties === "object") return true;
  return false;
}

function isArraySchema(schema: any): boolean {
  return primaryScalarType(schema) === "array" || Array.isArray(schema?.items);
}

// A `Quantity { value, unit? }` renders as one inline row (value input + unit
// dropdown) rather than an expandable object with two child rows.
function isQuantitySchema(schema: any): boolean {
  if (!isObjectSchema(schema)) return false;
  const props = schema?.properties;
  if (!props || typeof props !== "object") return false;
  const keys = Object.keys(props).filter((k) => !k.startsWith("__"));
  return keys.length === 2 && keys.includes("value") && keys.includes("unit");
}

export function findActiveBranchIndex(
  propSchema: any,
  dataValue: any
): number {
  const branches: any[] = Array.isArray(propSchema?.oneOf) ? propSchema.oneOf : [];
  if (branches.length === 0) return -1;
  if (dataValue == null || typeof dataValue !== "object") return 0;
  let hasDiscriminator = false;
  for (let i = 0; i < branches.length; i += 1) {
    const props = branches[i]?.properties ?? {};
    for (const [k, v] of Object.entries(props) as any[]) {
      if (v?.const !== undefined) {
        hasDiscriminator = true;
        if (dataValue[k] === v.const) return i;
      }
    }
  }
  // Discriminated branches but no match → ambiguous, don't guess branch 0.
  // An undiscriminated oneOf can't be resolved, so fall back to the first branch.
  return hasDiscriminator ? -1 : 0;
}

function buildBranchInfos(propSchema: any): OneOfBranchInfo[] {
  const branches: any[] = Array.isArray(propSchema?.oneOf) ? propSchema.oneOf : [];
  return branches.map((b, i) => {
    let discriminatorValue: string | number | null = null;
    const props = b?.properties ?? {};
    for (const v of Object.values(props) as any[]) {
      if (v?.const !== undefined) {
        discriminatorValue = v.const;
        break;
      }
    }
    const title =
      (typeof b?.title === "string" && b.title.trim()) ||
      (typeof discriminatorValue === "string" ? discriminatorValue : `Option ${i + 1}`);
    return { index: i, title, discriminatorValue, schema: b };
  });
}

function discriminatorKeyForBranch(branch: any): string | undefined {
  const props = branch?.properties ?? {};
  for (const [k, v] of Object.entries(props) as any[]) {
    if (v?.const !== undefined) return k;
  }
  return undefined;
}

function isVisibleOptionalKey(key: string): boolean {
  return !key.startsWith("__");
}

// "Effectively present" means: key exists in the data AND its value carries
// meaning (not null/undefined/""/{}/[]). Optional fields without an effective
// value belong in the ⋮ Add-optional-field menu, not as rendered rows.
function hasEffectiveValue(dataObj: Record<string, unknown> | undefined, key: string): boolean {
  if (!dataObj || !(key in dataObj)) return false;
  const v = dataObj[key];
  if (v === null || v === undefined || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Object.keys(v as object).length > 0;
  return true;
}

function buildOptionalChildren(
  propertiesSchema: Record<string, any>,
  requiredKeys: Set<string>,
  dataObj: Record<string, unknown>,
  parentPath: FieldPath,
  dirtyIds: Set<string>
): OptionalChildInfo[] {
  const list: OptionalChildInfo[] = [];
  for (const [key, propSchema] of Object.entries(propertiesSchema)) {
    if (!isVisibleOptionalKey(key)) continue;
    if (requiredKeys.has(key)) continue;
    if (hasEffectiveValue(dataObj, key)) continue;
    if (dirtyIds.has(pathToId([...parentPath, key]))) continue;
    if (propSchema?.const !== undefined) continue;
    const label = (propSchema?.title as string) || prettify(key);
    list.push({
      key,
      label,
      schema: propSchema,
      defaultValue: propSchema?.default,
    });
  }
  return list;
}

function fieldTypeForLeaf(schema: any, rootSchema: any): FieldType {
  const enumValues = getEnumFromSchema(schema, rootSchema);
  if (Array.isArray(enumValues) && enumValues.length > 0) return "enum";
  const t = primaryScalarType(schema);
  if (t === "string") return "string";
  if (t === "number") return "number";
  if (t === "integer") return "integer";
  if (t === "boolean") return "boolean";
  return "string";
}

interface WalkContext {
  rootSchema: any;
  dirtyIds: Set<string>;
  errorsByRowId: Map<string, { message: string; severity: "error" | "warning" }>;
  pendingDiffs: Map<string, import("./types").BranchDiff>;
}

function makeRow(opts: {
  path: FieldPath;
  parentId: string | null;
  fieldName: string;
  schema: any;
  value: unknown;
  fieldType: FieldType;
  required: boolean;
  ctx: WalkContext;
  overrides?: Partial<FormRow>;
}): FormRow {
  const { path, parentId, fieldName, schema, value, fieldType, required, ctx, overrides } = opts;
  const id = pathToId(path);
  const label = (schema?.title as string) || prettify(fieldName);
  const useEndpoint =
    typeof schema?.use_endpoint === "string" ? schema.use_endpoint : undefined;
  const isSecret = isSecretField({ schema, label, fieldName, path });
  const enumOptions =
    fieldType === "enum" ? enumOptionsFromSchema(schema, ctx.rootSchema) : undefined;
  const errEntry = ctx.errorsByRowId.get(id);
  return {
    id,
    path,
    parentId,
    fieldName,
    label,
    fieldType,
    value,
    defaultValue: schema?.default,
    required,
    schema,
    isOptionalSectionParent: false,
    optionalSectionEnabled: false,
    isAdditionalPropsParent: false,
    isAdditionalPropsChild: false,
    enumOptions,
    useEndpoint,
    isSecret,
    isDirty: ctx.dirtyIds.has(id),
    errorMessage: errEntry?.message,
    errorSeverity: errEntry?.severity,
    isDisabled: false,
    ...overrides,
  };
}

function walkProperty(
  key: string,
  propSchema: any,
  parentDataObj: Record<string, unknown> | undefined,
  parentPath: FieldPath,
  parentId: string | null,
  required: boolean,
  ctx: WalkContext
): FormRow {
  const path = [...parentPath, key];
  const value = parentDataObj?.[key];

  if (Array.isArray(propSchema?.oneOf) && propSchema.oneOf.length > 0) {
    return walkOneOf(key, propSchema, value, path, parentId, required, ctx);
  }
  if (isQuantitySchema(propSchema)) {
    return walkQuantity(key, propSchema, value, path, parentId, required, ctx);
  }
  if (isObjectSchema(propSchema)) {
    return walkObject(key, propSchema, value, path, parentId, required, ctx);
  }
  if (isArraySchema(propSchema)) {
    return walkArray(key, propSchema, value, path, parentId, required, ctx);
  }
  const leafType = fieldTypeForLeaf(propSchema, ctx.rootSchema);
  return makeRow({
    path,
    parentId,
    fieldName: key,
    schema: propSchema,
    value,
    fieldType: leafType,
    required,
    ctx,
  });
}

function walkOneOf(
  key: string,
  propSchema: any,
  value: unknown,
  path: FieldPath,
  parentId: string | null,
  required: boolean,
  ctx: WalkContext
): FormRow {
  const branches = buildBranchInfos(propSchema);
  const isOptionalSection = propSchema?.__optionalSection === true;
  const enabled = !(value == null);
  const id = pathToId(path);

  const row = makeRow({
    path,
    parentId,
    fieldName: key,
    schema: propSchema,
    value,
    fieldType: "oneOf",
    required,
    ctx,
    overrides: {
      oneOfBranches: branches,
      isOptionalSectionParent: isOptionalSection,
      optionalSectionEnabled: enabled,
    },
  });

  if (isOptionalSection && !enabled) {
    row.children = [];
    return row;
  }

  // No data yet → no branch picked; show no children until user picks one.
  const hasValue = value && typeof value === "object" && Object.keys(value as object).length > 0;
  if (!hasValue) {
    row.activeOneOfIndex = undefined;
    row.children = [];
    return row;
  }

  const activeIndex = findActiveBranchIndex(propSchema, value);
  row.activeOneOfIndex = activeIndex >= 0 ? activeIndex : 0;
  const activeBranch = branches[row.activeOneOfIndex]?.schema;
  row.discriminatorKey = discriminatorKeyForBranch(activeBranch);
  const branchProps = (activeBranch?.properties ?? {}) as Record<string, any>;
  const branchRequired = new Set<string>(
    Array.isArray(activeBranch?.required) ? activeBranch.required : []
  );
  const branchDataObj =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  row.optionalChildren = buildOptionalChildren(branchProps, branchRequired, branchDataObj, path, ctx.dirtyIds);
  const branchChildren = walkBranchChildren(activeBranch, value, path, id, ctx);

  const pendingDiff = ctx.pendingDiffs.get(id);
  if (pendingDiff) {
    const diffRow: FormRow = {
      id: `${id}.__diffPreview`,
      path: [...path, "__diffPreview"],
      parentId: id,
      fieldName: "__diffPreview",
      label: "",
      fieldType: "diffPreview",
      value: undefined,
      required: false,
      schema: {},
      isOptionalSectionParent: false,
      optionalSectionEnabled: false,
      isAdditionalPropsParent: false,
      isAdditionalPropsChild: false,
      isSecret: false,
      isDirty: false,
      isDisabled: false,
      diff: pendingDiff,
    };
    row.children = [diffRow, ...branchChildren];
  } else {
    row.children = branchChildren;
  }
  return row;
}

function walkBranchChildren(
  branchSchema: any,
  dataValue: unknown,
  parentPath: FieldPath,
  parentId: string,
  ctx: WalkContext
): FormRow[] {
  const props = (branchSchema?.properties ?? {}) as Record<string, any>;
  const required = new Set<string>(
    Array.isArray(branchSchema?.required) ? branchSchema.required : []
  );
  const dataObj =
    dataValue && typeof dataValue === "object" && !Array.isArray(dataValue)
      ? (dataValue as Record<string, unknown>)
      : {};

  const children: FormRow[] = [];
  for (const [key, schema] of Object.entries(props)) {
    if (!isVisibleOptionalKey(key)) continue;
    if (schema?.const !== undefined) continue;
    if (
      !required.has(key) &&
      !hasEffectiveValue(dataObj, key) &&
      !ctx.dirtyIds.has(pathToId([...parentPath, key]))
    )
      continue;
    children.push(
      walkProperty(
        key,
        schema,
        dataObj,
        parentPath,
        parentId,
        required.has(key),
        ctx
      )
    );
  }
  return children;
}

function walkQuantity(
  key: string,
  propSchema: any,
  value: unknown,
  path: FieldPath,
  parentId: string | null,
  required: boolean,
  ctx: WalkContext
): FormRow {
  const unitSchema = (propSchema?.properties as Record<string, any>)?.unit;
  const enumOptions = enumOptionsFromSchema(unitSchema, ctx.rootSchema);
  return makeRow({
    path,
    parentId,
    fieldName: key,
    schema: propSchema,
    value,
    fieldType: "quantity",
    required,
    ctx,
    overrides: { enumOptions },
  });
}

function walkObject(
  key: string,
  propSchema: any,
  value: unknown,
  path: FieldPath,
  parentId: string | null,
  required: boolean,
  ctx: WalkContext
): FormRow {
  const id = pathToId(path);
  const dataObj =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const props = (propSchema?.properties ?? {}) as Record<string, any>;
  const requiredKeys = new Set<string>(
    Array.isArray(propSchema?.required) ? propSchema.required : []
  );

  const optionalChildren = buildOptionalChildren(props, requiredKeys, dataObj, path, ctx.dirtyIds);

  const children: FormRow[] = [];
  for (const [childKey, childSchema] of Object.entries(props)) {
    if (!isVisibleOptionalKey(childKey)) continue;
    if (childSchema?.const !== undefined) continue;
    if (
      !requiredKeys.has(childKey) &&
      !hasEffectiveValue(dataObj, childKey) &&
      !ctx.dirtyIds.has(pathToId([...path, childKey]))
    )
      continue;
    children.push(
      walkProperty(
        childKey,
        childSchema,
        dataObj,
        path,
        id,
        requiredKeys.has(childKey),
        ctx
      )
    );
  }

  return makeRow({
    path,
    parentId,
    fieldName: key,
    schema: propSchema,
    value,
    fieldType: "object",
    required,
    ctx,
    overrides: {
      children,
      optionalChildren,
    },
  });
}

function walkArray(
  key: string,
  propSchema: any,
  value: unknown,
  path: FieldPath,
  parentId: string | null,
  required: boolean,
  ctx: WalkContext
): FormRow {
  const id = pathToId(path);
  const items: unknown[] = Array.isArray(value) ? value : [];
  const itemsSchema = propSchema?.items;
  const isAdditionalPropsParent = propSchema?.__fromAdditionalProperties === true;
  const itemIsObject = isObjectSchema(itemsSchema);

  const children: FormRow[] = items.map((item, idx) =>
    walkArrayItem(item, itemsSchema, idx, path, id, isAdditionalPropsParent, itemIsObject, ctx)
  );

  return makeRow({
    path,
    parentId,
    fieldName: key,
    schema: propSchema,
    value,
    fieldType: "array",
    required,
    ctx,
    overrides: {
      children,
      itemsSchema,
      isAdditionalPropsParent,
    },
  });
}

function walkArrayItem(
  itemValue: unknown,
  itemSchema: any,
  index: number,
  parentArrayPath: FieldPath,
  parentArrayId: string,
  isAdditionalPropsParent: boolean,
  itemIsObject: boolean,
  ctx: WalkContext
): FormRow {
  const path = [...parentArrayPath, index];
  const id = pathToId(path);

  if (!itemIsObject) {
    const leafType = fieldTypeForLeaf(itemSchema ?? {}, ctx.rootSchema);
    return makeRow({
      path,
      parentId: parentArrayId,
      fieldName: String(index),
      schema: itemSchema ?? {},
      value: itemValue,
      fieldType: leafType,
      required: true,
      ctx,
      overrides: { label: `[${index}]` },
    });
  }

  const dataObj =
    itemValue && typeof itemValue === "object" && !Array.isArray(itemValue)
      ? (itemValue as Record<string, unknown>)
      : {};
  const props = (itemSchema?.properties ?? {}) as Record<string, any>;
  const requiredKeys = new Set<string>(
    Array.isArray(itemSchema?.required) ? itemSchema.required : []
  );
  if (isAdditionalPropsParent) requiredKeys.add("__key");

  const optionalChildren = buildOptionalChildren(props, requiredKeys, dataObj, path, ctx.dirtyIds);

  const labelFromKey =
    isAdditionalPropsParent && typeof dataObj.__key === "string" && dataObj.__key.trim()
      ? String(dataObj.__key)
      : `Item ${index + 1}`;

  const children: FormRow[] = [];
  for (const [childKey, childSchema] of Object.entries(props)) {
    if (!isVisibleOptionalKey(childKey) && childKey !== "__key") continue;
    if (childSchema?.const !== undefined) continue;
    if (
      !requiredKeys.has(childKey) &&
      !hasEffectiveValue(dataObj, childKey) &&
      !ctx.dirtyIds.has(pathToId([...path, childKey]))
    )
      continue;
    const childRow = walkProperty(
      childKey,
      childSchema,
      dataObj,
      path,
      id,
      requiredKeys.has(childKey),
      ctx
    );
    if (isAdditionalPropsParent && childKey === "__key") {
      childRow.isAdditionalPropsChild = true;
      childRow.label = "Key";
    }
    children.push(childRow);
  }

  return makeRow({
    path,
    parentId: parentArrayId,
    fieldName: String(index),
    schema: itemSchema,
    value: itemValue,
    fieldType: "object",
    required: true,
    ctx,
    overrides: {
      label: labelFromKey,
      children,
      optionalChildren,
    },
  });
}

export function buildRows(opts: BuildRowsOpts): FormRow[] {
  const { schema, data, dirtyIds, errorsByRowId, pendingDiffs } = opts;
  if (!schema || !schema.properties) return [];
  const ctx: WalkContext = {
    rootSchema: schema,
    dirtyIds,
    errorsByRowId,
    pendingDiffs: pendingDiffs ?? new Map(),
  };
  const dataObj =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {};
  const props = schema.properties as Record<string, any>;
  const requiredKeys = new Set<string>(
    Array.isArray(schema.required) ? schema.required : []
  );

  const rows: FormRow[] = [];
  for (const [key, propSchema] of Object.entries(props)) {
    if (!isVisibleOptionalKey(key)) continue;
    if (propSchema?.const !== undefined) continue;
    if (
      !requiredKeys.has(key) &&
      !hasEffectiveValue(dataObj, key) &&
      !ctx.dirtyIds.has(pathToId([key]))
    )
      continue;
    rows.push(
      walkProperty(key, propSchema, dataObj, [], null, requiredKeys.has(key), ctx)
    );
  }
  // Heavy container fields (object/array/oneOf) go after scalar/enum fields so the
  // light top-level identity fields are visible without scrolling past Config.
  const isContainer = (r: FormRow) =>
    r.fieldType === "object" ||
    r.fieldType === "array" ||
    r.fieldType === "oneOf" ||
    r.fieldType === "anyOfTabs";
  return [...rows.filter((r) => !isContainer(r)), ...rows.filter(isContainer)];
}
